import React from 'react';
import { Compass, MapPin, Plus, Lock } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';

/**
 * ProspectPanel — panneau latéral « prospection » de la carte du réseau
 * (14/06/2026). Affiché quand le joueur clique sur une ville libre :
 * aperçu du rayon d'action (250 km), loyer estimé, contrats locaux qui
 * seraient débloqués, coût d'ouverture net, et CTA d'ouverture directe.
 *
 * @param {object} stats { city, rentEstimate, radiusKm, inRange, unlocked,
 *                         netCost, status: 'open'|'locked'|'quota_fr'|'quota_intl' }
 */
export default function ProspectPanel({ stats, onOpenDepot }) {
  const { t } = useTranslation();
  if (!stats) return null;
  const { city, status } = stats;
  const canOpen = status === 'open';

  return (
    <div className="border-t lg:border-t-0 lg:border-l border-ink-700 p-4 space-y-3" data-testid="depot-map-prospect-panel">
      <div>
        <p className="text-[10px] uppercase tracking-wider font-mono text-cyan-300">
          {t('depot_map_prospect_kind')}
        </p>
        <h4 className="text-base font-bold text-foreground flex items-center gap-2">
          <Compass className="w-4 h-4 text-cyan-300" />
          {city.name}
          {city.country !== 'FR' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 uppercase">{city.country}</span>
          )}
        </h4>
        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
          <MapPin className="w-3 h-3" /> {t('depot_map_prospect_title')}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 text-center">
        {[
          { label: t('depot_map_prospect_radius'), value: `${stats.radiusKm} km`, tid: 'radius' },
          { label: t('depot_map_prospect_rent'), value: `≈ ${formatMoney(stats.rentEstimate)}/m`, tid: 'rent' },
          { label: t('depot_map_prospect_inrange'), value: stats.inRange, tid: 'inrange' },
          { label: t('depot_map_prospect_unlocked'), value: `+${stats.unlocked}`, tid: 'unlocked', highlight: stats.unlocked > 0 },
        ].map(s => (
          <div key={s.tid} className="bg-ink-900/60 border border-ink-700 rounded-lg px-2 py-1.5" data-testid={`depot-prospect-stat-${s.tid}`}>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{s.label}</p>
            <p className={`text-sm font-bold font-mono ${s.highlight ? 'text-money' : 'text-foreground'}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs px-2 py-1.5 rounded border border-ink-700 bg-ink-900/40">
        <span className="text-slate-400">{t('depot_map_prospect_cost')}</span>
        <span className="font-mono font-bold text-amber-300">{formatMoney(stats.netCost)}</span>
      </div>

      {status === 'locked' && (
        <p className="text-[10px] text-warn flex items-center gap-1" data-testid="depot-prospect-locked-msg">
          <Lock className="w-3 h-3 flex-shrink-0" /> {t('depot_map_prospect_locked', { phase: city.phase })}
        </p>
      )}
      {status === 'quota_fr' && (
        <p className="text-[10px] text-warn flex items-center gap-1" data-testid="depot-prospect-quota-msg">
          <Lock className="w-3 h-3 flex-shrink-0" /> {t('depot_map_prospect_quota_fr')}
        </p>
      )}
      {status === 'quota_intl' && (
        <p className="text-[10px] text-warn flex items-center gap-1" data-testid="depot-prospect-quota-msg">
          <Lock className="w-3 h-3 flex-shrink-0" /> {t('depot_map_prospect_quota_intl')}
        </p>
      )}

      <button
        onClick={onOpenDepot}
        disabled={!canOpen}
        className="w-full text-xs font-bold py-2 rounded-lg bg-cyan-500/15 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
        data-testid="depot-prospect-open-btn"
      >
        <Plus className="w-3.5 h-3.5" /> {t('depot_map_prospect_open_btn')}
      </button>
    </div>
  );
}
